import * as React from 'react';
import { connect } from 'react-redux';
import * as Actions from './Actions';
import notify from '../../shared/feedback/notifications';
import { Translate } from '../../shared/LanguageService';
import {
    Button,
    ButtonToolbar,
    Modal,
    ControlLabel,
    FormControl,
    Row,
    Col,
    Checkbox
} from 'react-bootstrap';
import { IInvoiceProductTypeListItem, ISite } from './Types';
import { handleError } from '../../shared/ErrorCatching';
import ValidatableFormGroup from '../../components/common/ValidatableFormGroup';
import FormInputChangeHandler from '../../shared/form/FormInputChangeHandler';
import FormSelect from '../../components/common/FormSelect';

interface IInvoiceProductTypeModalProps {
    id?: number;
    load: () => void;
    close: () => void;
    actions: IInvoiceProductTypeModalActions;
    item: any;
}

interface IInvoiceProductTypeModalActions {
    load: () => Promise<void>;
    get: (id: number) => Promise<void>;
    create: (name: string, site: number) => Promise<void>;
    edit: (
        id: number,
        name: string,
        country: number,
        site: number,
        isSystemDefault: boolean
    ) => Promise<void>;
}

class InvoiceProductTypeModal extends React.Component<IInvoiceProductTypeModalProps, any> {
    formInputChangeHandler: FormInputChangeHandler;

    constructor(props: any) {
        super(props);

        this.state = {
            form: {
                name: '',
                country: null,
                site: null,
                isSystemDefault: false
            },
            sites: [] as ISite[],
            errors: {}
        };

        this.save = this.save.bind(this);
        this.formInputChangeHandler = new FormInputChangeHandler(this.state.form, (newState) =>
            this.setState({ form: newState })
        );
    }

    componentDidMount(): void {
        if (this.props.id) {
            this.props.actions
                .get(this.props.id)
                .then(() => {
                    this.setState((state, props) => {
                        return {
                            form: {
                                name: props.item.name,
                                country: props.item.country,
                                site: props.item.site,
                                isSystemDefault: props.item.isSystemDefault
                            },
                            sites: this.props.item.sites
                        };
                    });
                    this.formInputChangeHandler.updateObj({
                        name: this.props.item.name,
                        country: this.props.item.country,
                        site: this.props.item.site,
                        isSystemDefault: this.props.item.isSystemDefault
                    });
                })
                .catch((error: any) => handleError(error));
        } else {
            this.props.actions
                .load()
                .then(() => this.setState({ sites: this.props.item.sites }))
                .catch((error: any) => handleError(error));
        }
    }

    save(): void {
        let action = (name, country, site, isSystemDefault) =>
            this.props.id
                ? this.props.actions
                      .edit(this.props.id, name, country, site, isSystemDefault)
                      .then(() =>
                          notify.success(
                              Translate(
                                  'invoice-product-type/modal/notification/invoice-product-type-edited'
                              )
                          )
                      )
                : this.props.actions
                      .create(name, site)
                      .then(() =>
                          notify.success(
                              Translate(
                                  'invoice-product-type/modal/notification/invoice-product-type-created'
                              )
                          )
                      );

        action(
            this.state.form.name,
            this.state.form.country,
            this.state.form.site,
            this.state.form.isSystemDefault
        )
            .then(() => {
                this.setState({
                    form: { name: '', site: null },
                    errors: {},
                    sites: []
                });
                this.props.close();
                this.props.load();
            })
            .catch((error: any) =>
                handleError(error, (errors: any) => {
                    this.setState({ errors });
                })
            );
    }

    render() {
        return (
            <Modal show={true} onHide={this.props.close}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {this.props.id
                            ? Translate(
                                  'invoice-product-type/modal/title/edit-invoice-product-type'
                              )
                            : Translate(
                                  'invoice-product-type/modal/title/add-invoice-product-type'
                              )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form>
                        <fieldset>
                            <legend>{Translate('invoice-product-type/modal/form/legend')}</legend>
                            <Row>
                                <Col md={6}>
                                    <ValidatableFormGroup
                                        required={true}
                                        validationKey="name"
                                        errors={this.state.errors}
                                    >
                                        <ControlLabel>
                                            {Translate(
                                                'invoice-product-type/modal/form/input-label/invoice-product-type'
                                            )}
                                        </ControlLabel>
                                        <FormControl
                                            autoFocus
                                            type="text"
                                            name="name"
                                            value={this.state.form.name}
                                            onChange={this.formInputChangeHandler.onChange}
                                        />
                                    </ValidatableFormGroup>
                                </Col>
                                <Col md={4}>
                                    <ValidatableFormGroup
                                        required={true}
                                        validationKey="site"
                                        errors={this.state.errors}
                                    >
                                        <ControlLabel>
                                            {Translate(
                                                'invoice-product-type/modal/form/input-label/site'
                                            )}
                                        </ControlLabel>
                                        <FormSelect
                                            name="site"
                                            value={this.state.form.site}
                                            placeholder=""
                                            onChange={this.formInputChangeHandler.onChange}
                                            defaultTranslateKey="invoice-product-type/modal/form/site-select/default"
                                        >
                                            {this.state.sites.map((x: any) => {
                                                return (
                                                    <option key={x.id} value={x.id}>
                                                        {x.name}
                                                    </option>
                                                );
                                            })}
                                        </FormSelect>
                                    </ValidatableFormGroup>
                                </Col>
                                {this.props.id ? (
                                    <Col md={2}>
                                        <ValidatableFormGroup
                                            required={false}
                                            validationKey="isSystemDefault"
                                            errors={this.state.errors}
                                        >
                                            <ControlLabel>
                                                {Translate(
                                                    'invoice-product-type/modal/form/input-label/is-system-default'
                                                )}
                                            </ControlLabel>
                                            <Checkbox
                                                name="isSystemDefault"
                                                checked={this.state.form.isSystemDefault}
                                                onChange={this.formInputChangeHandler.onChange}
                                            >
                                                <span></span>
                                            </Checkbox>
                                        </ValidatableFormGroup>
                                    </Col>
                                ) : null}
                            </Row>
                        </fieldset>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={this.props.close}>
                        {Translate('invoice-product-type/modal/footer-button/close')}
                    </Button>
                    <Button bsStyle="primary" onClick={this.save}>
                        {Translate('invoice-product-type/modal/footer-button/save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

function mapStateToProps(state: any, ownProps: any) {
    return {
        item: state.invoiceProductType.item
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            load: () => dispatch(Actions.load()),
            get: (id) => dispatch(Actions.get(id)),
            create: (name, site) => dispatch(Actions.create(name, site)),
            edit: (id, name, country, site, isSystemDefault) =>
                dispatch(Actions.edit(id, name, country, site, isSystemDefault))
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceProductTypeModal);
